import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { getSizeWithinBounds } from '@folklore/size';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo, useCallback } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import Image from '@micromag/element-image';
import Video from '@micromag/element-video';

var styles = {"container":"micromag-element-visual-container","videoContainer":"micromag-element-visual-videoContainer","videoTag":"micromag-element-visual-videoTag","videoTouchOverlay":"micromag-element-visual-videoTouchOverlay"};

var _excluded = ["media", "mediaRef", "width", "height", "resolution", "objectFit", "playing", "muted", "shouldLoad", "videoLoop", "videoInitialMuted", "onLoaded", "className", "imageClassName", "videoClassName", "withoutVideo", "qualityStartLevel", "onQualityLevelChange"];
var propTypes = {
  media: PropTypes$1.media,
  mediaRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    current: PropTypes.any
  })]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  resolution: PropTypes.number,
  objectFit: PropTypes$1.objectFit,
  playing: PropTypes.bool,
  muted: PropTypes.bool,
  shouldLoad: PropTypes.bool,
  videoLoop: PropTypes.bool,
  withoutVideo: PropTypes.bool,
  videoInitialMuted: PropTypes.bool,
  onLoaded: PropTypes.func,
  className: PropTypes.string,
  imageClassName: PropTypes.string,
  videoClassName: PropTypes.string,
  qualityStartLevel: PropTypes.number,
  onQualityLevelChange: PropTypes.func
};
var defaultProps = {
  media: null,
  mediaRef: null,
  width: null,
  height: null,
  resolution: 1,
  objectFit: null,
  playing: true,
  muted: true,
  shouldLoad: true,
  videoLoop: true,
  withoutVideo: false,
  videoInitialMuted: true,
  onLoaded: null,
  className: null,
  imageClassName: null,
  videoClassName: null,
  qualityStartLevel: null,
  onQualityLevelChange: null
};
var Visual = function Visual(_ref) {
  var media = _ref.media,
    mediaRef = _ref.mediaRef,
    width = _ref.width,
    height = _ref.height,
    resolution = _ref.resolution,
    objectFit = _ref.objectFit,
    playing = _ref.playing,
    muted = _ref.muted,
    shouldLoad = _ref.shouldLoad,
    videoLoop = _ref.videoLoop;
    _ref.videoInitialMuted;
    var onParentLoaded = _ref.onLoaded,
    className = _ref.className,
    imageClassName = _ref.imageClassName,
    videoClassName = _ref.videoClassName,
    withoutVideo = _ref.withoutVideo,
    qualityStartLevel = _ref.qualityStartLevel,
    onQualityLevelChange = _ref.onQualityLevelChange,
    props = _objectWithoutProperties(_ref, _excluded);
  var _ref2 = media || {},
    _ref2$type = _ref2.type,
    type = _ref2$type === void 0 ? null : _ref2$type,
    _ref2$thumbnail_url = _ref2.thumbnail_url,
    thumbnailUrl = _ref2$thumbnail_url === void 0 ? null : _ref2$thumbnail_url,
    _ref2$url = _ref2.url,
    url = _ref2$url === void 0 ? null : _ref2$url;
  var isVideo = type === 'video';
  var elProps = useMemo(function () {
    return _objectSpread(_objectSpread({}, props), {}, {
      media: media
    });
  }, [props, media]);
  var imageElProps = useMemo(function () {
    var tmpProps = (!shouldLoad || withoutVideo) && isVideo ? _objectSpread(_objectSpread({}, elProps), {}, {
      media: {
        url: thumbnailUrl
      }
    }) : elProps;
    return shouldLoad && !withoutVideo ? _objectSpread(_objectSpread({}, elProps), {}, {
      media: _objectSpread(_objectSpread({}, media), {}, {
        url: url
      })
    }) : tmpProps;
  }, [isVideo, elProps, thumbnailUrl, url, shouldLoad]);
  var videoContainerStyle = null;
  if (type === 'video' && objectFit !== null && shouldLoad) {
    var _ref3 = objectFit || {},
      _ref3$fit = _ref3.fit,
      fit = _ref3$fit === void 0 ? 'cover' : _ref3$fit;
    var _ref4 = media || {},
      _ref4$metadata = _ref4.metadata,
      videoMetadata = _ref4$metadata === void 0 ? null : _ref4$metadata;
    var _ref5 = videoMetadata || {},
      _ref5$width = _ref5.width,
      videoWidth = _ref5$width === void 0 ? 0 : _ref5$width,
      _ref5$height = _ref5.height,
      videoHeight = _ref5$height === void 0 ? 0 : _ref5$height;
    var _getSizeWithinBounds = getSizeWithinBounds(videoWidth, videoHeight, width, height, {
        cover: fit === 'cover'
      }),
      resizedVideoWidth = _getSizeWithinBounds.width,
      resizedVideoHeight = _getSizeWithinBounds.height;
    var resizedVideoLeft = -(resizedVideoWidth - width) / 2;
    var resizedVideoTop = -(resizedVideoHeight - height) / 2;
    videoContainerStyle = {
      width: resizedVideoWidth,
      height: resizedVideoHeight,
      left: resizedVideoLeft,
      top: resizedVideoTop
    };
  }
  var onLoaded = useCallback(function (e) {
    if (onParentLoaded !== null) {
      onParentLoaded(e);
    }
  }, []);
  return type !== null ? /*#__PURE__*/React.createElement(React.Fragment, null, type === 'image' || !shouldLoad || withoutVideo ? /*#__PURE__*/React.createElement(Image, Object.assign({}, imageElProps, {
    objectFit: objectFit,
    width: width,
    height: height,
    resolution: resolution,
    shouldLoad: shouldLoad,
    onLoaded: onLoaded,
    className: classNames([styles.container, _defineProperty({}, className, className !== null)]),
    imageClassName: imageClassName
  })) : null, type === 'video' && shouldLoad && !withoutVideo ? /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty({}, className, className !== null)]),
    style: {
      width: width,
      height: height
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.videoContainer, _defineProperty({}, videoClassName, videoClassName !== null)]),
    style: videoContainerStyle
  }, /*#__PURE__*/React.createElement(Video, Object.assign({}, elProps, {
    innerClassName: styles.videoTag,
    mediaRef: mediaRef,
    width: objectFit === null ? width : null,
    height: objectFit === null ? height : null,
    paused: !playing,
    muted: muted,
    loop: videoLoop,
    shouldLoad: shouldLoad,
    onReady: onLoaded,
    autoPlay: true,
    qualityStartLevel: qualityStartLevel,
    onQualityLevelChange: onQualityLevelChange
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.videoTouchOverlay
  }))) : null) : null;
};
Visual.propTypes = propTypes;
Visual.defaultProps = defaultProps;

export { Visual as default };
